(() => {
  'use strict';

  const formatMoney = (cents) => {
    const amount = (Number(cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return (window.theme.moneyFormat || '${{amount}}').replace(/\{\{\s*amount(?:_no_decimals)?\s*\}\}/, amount);
  };

  class ProductSection {
    constructor(section) {
      if (section.dataset.productInitialized) return;
      section.dataset.productInitialized = 'true';
      this.section = section;
      this.form = section.querySelector('[data-product-form]');
      this.variants = JSON.parse(section.querySelector('[data-product-variants]')?.textContent || '[]');
      this.currentVariant = this.variants.find((variant) => Number(variant.id) === Number(this.form?.querySelector('[data-variant-id]')?.value));
      this.bind();
    }

    bind() {
      this.form?.querySelectorAll('[data-option-position]').forEach((input) => input.addEventListener('change', () => this.onOptionsChanged()));
      this.section.querySelectorAll('[data-media-target]').forEach((button) => button.addEventListener('click', () => this.showMedia(button.dataset.mediaTarget)));
      this.section.querySelectorAll('[data-product-zoom]').forEach((button) => button.addEventListener('click', () => this.openZoom(button)));
      this.section.querySelector('[data-product-zoom-close]')?.addEventListener('click', () => this.section.querySelector('[data-product-zoom-modal]')?.close());
      this.section.querySelector('[data-sticky-submit]')?.addEventListener('click', () => this.form?.requestSubmit());
    }

    selectedOptions() {
      const values = [];
      const positions = [...new Set([...this.form.querySelectorAll('[data-option-position]')].map((input) => Number(input.dataset.optionPosition)))].sort();
      positions.forEach((position) => {
        const inputs = [...this.form.querySelectorAll(`[data-option-position="${position}"]`)];
        const selected = inputs.find((input) => input.matches('select') || input.checked);
        values.push(selected?.value);
        const legend = selected?.closest('fieldset')?.querySelector('[data-option-label]');
        if (legend) legend.textContent = selected?.value || '';
      });
      return values;
    }

    onOptionsChanged() {
      const selected = this.selectedOptions();
      this.currentVariant = this.variants.find((variant) => variant.options.every((option, index) => option === selected[index]));
      this.update();
    }

    update() {
      const variant = this.currentVariant;
      const addButton = this.form.querySelector('[data-add-to-cart]');
      const addText = this.form.querySelector('[data-add-to-cart-text]');
      const stickyButton = this.section.querySelector('[data-sticky-submit]');
      const stickyText = this.section.querySelector('[data-sticky-text]');
      const variantInput = this.form.querySelector('[data-variant-id]');
      if (!variant) {
        if (addButton) addButton.disabled = true;
        if (addText) addText.textContent = 'Unavailable';
        if (stickyButton) stickyButton.disabled = true;
        if (stickyText) stickyText.textContent = 'Unavailable';
        return;
      }
      if (variantInput) variantInput.value = variant.id;
      const available = Boolean(variant.available);
      if (addButton) addButton.disabled = !available;
      if (addText) addText.textContent = available ? 'Add to cart' : 'Sold out';
      if (stickyButton) stickyButton.disabled = !available;
      if (stickyText) stickyText.textContent = available ? 'Add to cart' : 'Sold out';
      const current = this.section.querySelector('[data-price] .price__current');
      const compare = this.section.querySelector('[data-price] .price__compare');
      const savings = this.section.querySelector('[data-price] .price__savings');
      if (current) current.textContent = formatMoney(variant.price);
      if (compare) {
        compare.textContent = variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price) : '';
        compare.hidden = !(variant.compare_at_price > variant.price);
      }
      if (savings) {
        savings.textContent = variant.compare_at_price > variant.price ? `Save ${formatMoney(variant.compare_at_price - variant.price)}` : '';
        savings.hidden = !(variant.compare_at_price > variant.price);
      }
      const stickyPrice = this.section.querySelector('[data-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
      const sku = this.section.querySelector('[data-sku]');
      if (sku) sku.textContent = variant.sku || '—';
      const inventory = this.section.querySelector('[data-inventory]');
      if (inventory) {
        const threshold = Number(inventory.dataset.lowStockThreshold || 0);
        const quantity = Number(variant.inventory_quantity);
        inventory.textContent = !available ? 'Out of stock' : (Number.isFinite(quantity) && quantity > 0 && quantity <= threshold ? `Only ${quantity} left` : 'In stock');
        inventory.style.color = available ? 'var(--color-success)' : 'var(--color-error)';
      }
      this.section.querySelector('[data-back-in-stock-hook]')?.toggleAttribute('hidden', available);
      if (variant.featured_media?.id) this.showMedia(variant.featured_media.id);
      const url = new URL(location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
    }

    showMedia(id) {
      this.section.querySelectorAll('[data-media-id]').forEach((media) => {
        const active = String(media.dataset.mediaId) === String(id);
        media.hidden = !active;
        media.classList.toggle('is-active', active);
        if (!active) media.querySelectorAll('video').forEach((video) => video.pause());
      });
      this.section.querySelectorAll('[data-media-target]').forEach((button) => {
        const active = String(button.dataset.mediaTarget) === String(id);
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }

    openZoom(button) {
      if (button.disabled) return;
      const source = button.querySelector('img');
      const dialog = this.section.querySelector('[data-product-zoom-modal]');
      const target = dialog?.querySelector('[data-product-zoom-image]');
      if (!source || !dialog || !target) return;
      target.src = source.currentSrc || source.src;
      target.alt = source.alt;
      dialog.showModal();
    }
  }

  const init = (scope = document) => scope.querySelectorAll('[data-product-section]').forEach((section) => new ProductSection(section));
  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
